/**
 * Pure selection helpers for the Outside (collision) protected-structure set.
 *
 * These encode the cross-constraint invariants that keep a stroke from being
 * silently erased or bound to a stale ROI. They live outside the React
 * component so the clinically load-bearing rules can be unit-tested directly,
 * rather than only through component effects a future caller could bypass.
 *
 * Each transform returns the SAME array reference when nothing changed, so
 * callers can guard state updates on identity and avoid redundant re-renders.
 */

/**
 * Remove the active Inside bound from the protected set. A structure that is
 * both the Inside bound and Outside-protected would be intersected with, then
 * subtracted from, the stroke at clip time — erasing it entirely — so it must
 * never appear in both. (The engine also enforces this; this keeps the UI in
 * sync so the checkbox never shows a self-erasing selection.)
 */
export function dropBoundingFromProtected(
    protectedRoiNumbers: number[],
    activeBoundingRoi: number | null
): number[] {
    if (
        activeBoundingRoi === null ||
        !protectedRoiNumbers.includes(activeBoundingRoi)
    ) {
        return protectedRoiNumbers;
    }
    return protectedRoiNumbers.filter((n) => n !== activeBoundingRoi);
}

/**
 * Drop protected roiNumbers that no longer exist in the active structure set.
 * roiNumbers are unique only within a set, so a stale selection (e.g. an ROI
 * deleted mid-session) could otherwise silently protect the wrong structure.
 */
export function pruneProtectedToExisting(
    protectedRoiNumbers: number[],
    existingRoiNumbers: Iterable<number>
): number[] {
    const existing = new Set(existingRoiNumbers);
    const pruned = protectedRoiNumbers.filter((n) => existing.has(n));
    return pruned.length === protectedRoiNumbers.length
        ? protectedRoiNumbers
        : pruned;
}

/**
 * Add (checked) or remove (unchecked) every approved structure from the
 * protected set, leaving any manually-checked non-approved structures
 * untouched. Existing entries keep their order; newly added approved entries
 * are appended.
 */
export function toggleProtectAllApproved(
    protectedRoiNumbers: number[],
    approvedRoiNumbers: number[],
    checked: boolean
): number[] {
    const set = new Set(protectedRoiNumbers);
    for (const n of approvedRoiNumbers) {
        if (checked) {
            set.add(n);
        } else {
            set.delete(n);
        }
    }
    return [...set];
}

/** Tri-state of the "Protect all approved" checkbox. */
export interface ApprovedProtectionState {
    /** At least one approved structure is eligible for protection. */
    hasApproved: boolean;
    /** Every eligible approved structure is currently protected. */
    allApprovedProtected: boolean;
    /** Some (but not necessarily all) approved structures are protected. */
    someApprovedProtected: boolean;
}

/**
 * Derive the "Protect all approved" checkbox state from the eligible approved
 * structures and the current protected selection.
 */
export function computeApprovedProtectionState(
    approvedRoiNumbers: number[],
    protectedRoiNumbers: number[]
): ApprovedProtectionState {
    const hasApproved = approvedRoiNumbers.length > 0;
    const protectedSet = new Set(protectedRoiNumbers);
    return {
        hasApproved,
        allApprovedProtected:
            hasApproved && approvedRoiNumbers.every((n) => protectedSet.has(n)),
        someApprovedProtected:
            hasApproved && approvedRoiNumbers.some((n) => protectedSet.has(n)),
    };
}
